/**
 * Sentry frontend init — fail-safe, dormant when REACT_APP_SENTRY_DSN unset.
 *
 * Loaded synchronously at the very top of `index.js` so Sentry can capture
 * errors thrown during initial module evaluation. If the DSN is absent,
 * this file is a complete no-op (no client, no console output, no
 * performance overhead).
 *
 * We deliberately disable Sentry session replay because PostHog already
 * provides session recording (privacy-hardened in lib/posthog.js). Running
 * both would duplicate session data.
 *
 * Configure via env at build time:
 *   REACT_APP_SENTRY_DSN  — DSN from your Sentry project (silent if unset)
 *   REACT_APP_ENV         — default "production"; tags every event
 */
import * as Sentry from "@sentry/react";

const DSN = process.env.REACT_APP_SENTRY_DSN;
const ENV = process.env.REACT_APP_ENV || "production";

const REDACT_PATH_FRAGMENTS = [
  "/api/billing/",
  "/api/admin/",
  "/api/auth/",
  "/api/account/login",
  "/api/account/signup",
  "/api/account/password",
];

function scrubEvent(event) {
  try {
    const req = event.request;
    if (req) {
      // Cookies → out.
      delete req.cookies;
      // Sensitive headers → out.
      if (req.headers) {
        ["Authorization", "authorization", "Cookie", "cookie",
         "X-API-Key", "x-api-key", "X-Auth-Token", "x-auth-token"]
          .forEach((h) => delete req.headers[h]);
      }
      // Redact request body for sensitive endpoints.
      const url = req.url || "";
      if (REDACT_PATH_FRAGMENTS.some((p) => url.includes(p))) {
        if ("data" in req) req.data = "[REDACTED]";
      }
    }
  } catch (_) {
    // Scrub must never throw.
  }
  return event;
}

if (DSN) {
  try {
    Sentry.init({
      dsn: DSN,
      environment: ENV,
      // Errors only — no perf transactions, no replay (PostHog handles replay).
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      // Quiet in production console.
      debug: false,
      beforeSend: scrubEvent,
      // Filter the predictable noise.
      ignoreErrors: [
        // Browser-extension / third-party noise we can't fix.
        "top.GLOBALS",
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        // Network blips when users go offline mid-request — not actionable.
        "Network request failed",
        "Failed to fetch",
        "Load failed",
        "AbortError",
        // Common iframe / cross-origin script error noise.
        "Script error.",
      ],
    });
  } catch (err) {
    // Never let analytics break the app boot.
    // eslint-disable-next-line no-console
    console.error("[sentry] init failed:", err);
  }
}

export const isSentryEnabled = Boolean(DSN);
export { Sentry };
